import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  InlineAlert,
  Input,
  ModalShell,
  Select,
  Textarea,
} from "@workspace/ui";
import { isAxiosError } from "axios";

export interface FeedbackInput {
  type: "bug" | "support" | "suggestion";
  subject: string;
  description: string;
  screenshot?: File;
}

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: FeedbackInput) => Promise<{ reference: string }>;
}

export function FeedbackDialog({
  open,
  onClose,
  onSubmit,
}: FeedbackDialogProps) {
  const { t } = useTranslation("layout");
  const id = useId();
  const submittingRef = useRef(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [type, setType] = useState<FeedbackInput["type"]>("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<File>();
  const [preview, setPreview] = useState<string>();
  const [fileError, setFileError] = useState<string>();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string>();

  useEffect(() => {
    if (open && reference) closeButtonRef.current?.focus();
  }, [open, reference]);

  useEffect(() => {
    if (!screenshot || !open) {
      setPreview(undefined);
      return;
    }
    const url = URL.createObjectURL(screenshot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [open, screenshot]);

  const close = () => {
    if (submittingRef.current) return;
    setReference(undefined);
    onClose();
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || fileError) return;
    if (!subject.trim() || !description.trim()) {
      setError("required");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await onSubmit({
        type,
        subject: subject.trim(),
        description: description.trim(),
        screenshot,
      });
      setReference(result.reference);
      setType("bug");
      setSubject("");
      setDescription("");
      setScreenshot(undefined);
    } catch (failure) {
      const status = isAxiosError(failure)
        ? failure.response?.status
        : undefined;
      setError(
        status === 429
          ? "rateLimit"
          : status === 503
            ? "unavailable"
            : status === 400 || status === 413
              ? "invalid"
              : "send",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={close}
      title={t("feedback.title")}
      subtitle={t("feedback.subtitle")}
      maxWidth="max-w-xl"
      scrollable
      showCloseButton={false}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button
            ref={closeButtonRef}
            type="button"
            variant="secondary"
            onClick={close}
            disabled={submitting}
          >
            {t("feedback.close")}
          </Button>
          {!reference ? (
            <Button
              type="submit"
              form={`${id}-form`}
              disabled={submitting || Boolean(fileError)}
            >
              {t(submitting ? "feedback.sending" : "feedback.send")}
            </Button>
          ) : null}
        </div>
      }
    >
      {reference ? (
        <InlineAlert tone="success" title={t("feedback.success.title")}>
          <p>{t("feedback.success.description")}</p>
          <p className="mt-2 break-all font-mono text-xs">
            {t("feedback.success.reference", { reference })}
          </p>
        </InlineAlert>
      ) : (
        <form id={`${id}-form`} onSubmit={submit} aria-busy={submitting}>
          <fieldset disabled={submitting} className="min-w-0 space-y-4">
            <div className="space-y-1">
              <label
                htmlFor={`${id}-type`}
                className="text-sm font-medium text-content-secondary"
              >
                {t("feedback.type")}
              </label>
              <Select
                id={`${id}-type`}
                value={type}
                onChange={(event) =>
                  setType(event.target.value as FeedbackInput["type"])
                }
              >
                <option value="bug">{t("feedback.types.bug")}</option>
                <option value="support">{t("feedback.types.support")}</option>
                <option value="suggestion">
                  {t("feedback.types.suggestion")}
                </option>
              </Select>
            </div>
            <div className="space-y-1">
              <label
                htmlFor={`${id}-subject`}
                className="text-sm font-medium text-content-secondary"
              >
                {t("feedback.subject")}
              </label>
              <Input
                id={`${id}-subject`}
                required
                maxLength={200}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor={`${id}-description`}
                className="text-sm font-medium text-content-secondary"
              >
                {t("feedback.description")}
              </label>
              <Textarea
                id={`${id}-description`}
                required
                maxLength={4000}
                rows={5}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                aria-describedby={`${id}-hint`}
              />
              <p id={`${id}-hint`} className="text-xs text-content-muted">
                {t(`feedback.hints.${type}`)}
              </p>
            </div>
            <div className="space-y-2">
              <label
                htmlFor={`${id}-screenshot`}
                className="text-sm font-medium text-content-secondary"
              >
                {t("feedback.screenshot")}
              </label>
              <Input
                id={`${id}-screenshot`}
                type="file"
                hidden
                className="hidden"
                accept="image/png,image/jpeg,image/webp"
                aria-describedby={`${id}-file-hint${fileError ? ` ${id}-file-error` : ""}`}
                aria-invalid={Boolean(fileError)}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (!file) return;
                  if (
                    !["image/png", "image/jpeg", "image/webp"].includes(
                      file.type,
                    ) ||
                    file.size === 0 ||
                    file.size > 5 * 1024 * 1024
                  ) {
                    setFileError("file");
                    return;
                  }
                  setFileError(undefined);
                  setScreenshot(file);
                }}
              />
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  aria-describedby={`${id}-file-hint${fileError ? ` ${id}-file-error` : ""}`}
                  onClick={() =>
                    document.getElementById(`${id}-screenshot`)?.click()
                  }
                >
                  {t("feedback.attach")}
                </Button>
              </div>
              <p id={`${id}-file-hint`} className="text-xs text-content-muted">
                {t("feedback.fileHint")}
              </p>
              {fileError ? (
                <p
                  id={`${id}-file-error`}
                  role="alert"
                  className="text-sm text-state-danger"
                >
                  {t("feedback.errors.file")}
                </p>
              ) : null}
              {fileError && !screenshot ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setFileError(undefined)}
                >
                  {t("feedback.remove")}
                </Button>
              ) : null}
              {screenshot ? (
                <div className="space-y-2 rounded-lg border border-border-subtle p-3">
                  {preview ? (
                    <img
                      src={preview}
                      alt={t("feedback.preview")}
                      className="max-h-40 w-full rounded-md object-contain"
                    />
                  ) : null}
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 break-all text-xs text-content-secondary">
                      {screenshot.name}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setScreenshot(undefined);
                        setFileError(undefined);
                      }}
                    >
                      {t("feedback.remove")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
            <p className="text-xs text-content-muted">
              {t("feedback.privacy")}
            </p>
            {error ? (
              <InlineAlert tone="danger">
                {t(`feedback.errors.${error}`)}
              </InlineAlert>
            ) : null}
            {submitting ? (
              <p role="status" className="text-sm text-content-muted">
                {t("feedback.sending")}
              </p>
            ) : null}
          </fieldset>
        </form>
      )}
    </ModalShell>
  );
}
